import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-b from-background to-muted" data-testid="contact-hero">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">Get in Touch</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card">
              <h2 className="text-3xl font-heading font-bold mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Your Name *</label>
                  <Input
                    data-testid="contact-name-input"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Email *</label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Message *</label>
                  <Textarea
                    data-testid="contact-message-input"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-accent w-full flex items-center justify-center space-x-2"
                  data-testid="contact-submit-button"
                >
                  <Send className="w-5 h-5" />
                  <span>{loading ? 'Sending...' : 'Send Message'}</span>
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="feature-card">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-4 rounded-full">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold mb-2">Email Us</h3>
                    <p className="text-muted-foreground">hello@seedoza.com</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      We'll respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              <div className="feature-card">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-4 rounded-full">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold mb-2">Visit Us</h3>
                    <p className="text-muted-foreground">Bangalore, India</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Monday - Friday: 9AM - 6PM
                    </p>
                  </div>
                </div>
              </div>

              <div className="feature-card">
                <h3 className="text-xl font-heading font-semibold mb-4">Follow Us</h3>
                <p className="text-muted-foreground mb-4">
                  Stay updated with our latest products and health tips
                </p>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
                >
                  <span className="font-medium">Follow on Instagram</span>
                </a>
              </div>

              <div className="bg-primary text-primary-foreground rounded-3xl p-8">
                <h3 className="text-2xl font-heading font-bold mb-4">Need Immediate Help?</h3>
                <p className="mb-4">
                  Check out our FAQ page for quick answers to common questions.
                </p>
                <a href="/faq">
                  <button className="bg-white text-primary hover:bg-white/90 rounded-full px-6 py-3 font-semibold transition-colors">
                    View FAQ
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
